import { getFormattedDate } from './locale'
import { ReactComponent as Protected } from '../assets/images/protected.svg'
import { ReactComponent as NotProtected } from '../assets/images/not-protected.svg'
import { ReactComponent as OnHold } from '../assets/images/on-hold.svg'

import { Button } from 'sfy-react'
import styles from './claims.module.scss'

const coverageAndNextBillingDate = {
  dateLabel: 'coverageStartDate',
  dateLabel2: 'nextBillingDate',
  planDetailsDate1: 'coverageStartDate',
  planDetailsDate2: 'nextBillingDate',
}

const coverageStartAndEndDate = {
  dateLabel: 'coverageStartDate',
  dateLabel2: 'coverageEndDate',
  planDetailsDate1: 'coverageStartDate',
  planDetailsDate2: ['coverageEndDate', 'validityDate'],
}

const planPurchaseAndCancellationDate = {
  dateLabel: 'planPurchaseDate',
  dateLabel2: 'cancellationDate',
  planDetailsDate1: 'dateOfPurchase',
  planDetailsDate2: 'planCancellationDate',
}

const cancellationAndCoverageEndDate = {
  dateLabel: 'cancellationDate',
  dateLabel2: 'coverageEndDate',
  planDetailsDate1: 'planCancellationDate',
  planDetailsDate2: 'coverageEndDate',
}

const planPurchaseAndCoverageEndDate = {
  dateLabel: 'planPurchaseDate',
  dateLabel2: 'coverageEndDate',
  planDetailsDate1: 'dateOfPurchase',
  planDetailsDate2: 'coverageEndDate',
}

const coverageAndValidity = {
  dateLabel: 'coverageStartDate',
  dateLabel2: 'coverageEndDate',
  planDetailsDate1: 'dateOfPurchase',
  planDetailsDate2: 'validityDate',
}

export {
  coverageAndNextBillingDate,
  coverageStartAndEndDate,
  planPurchaseAndCancellationDate,
  planPurchaseAndCoverageEndDate,
  cancellationAndCoverageEndDate,
  coverageAndValidity,
}

export const eligibilityChecks = {
  ALREADY_ENROLLED: {
    title: 'planPurchaseRestricted',
    message: 'alreadyEnrolled',
  },
  CONTRACT_UNDER_CANCELLATION: {
    title: 'planPurchaseRestricted',
    message: 'contractUnderCancellation',
  },
  CONTRACT_ON_HOLD: {
    title: 'planPurchaseRestricted',
    message: 'contractOnHold',
  },
}

const deviceStatusMapping = (consumerServiceRequestID, statement1, statement2, statement3) => {
  switch (true) {
    case consumerServiceRequestID === 0:
      return statement1
    case consumerServiceRequestID > 0:
      return statement2
    default:
      return statement3
  }
}

const activeStatuses = {
  myPlansVariableData1: (translatedData, planDetails) => {
    return `${planDetails.currencySymbol}${translatedData['plan']['amountDueOn'].replace(
      /@amount|@nextBillingDate/g,
      (matched) => planDetails[matched]
    )}`
  },
  myPlansVariableData2: (translatedData, numberOfProducts) => {
    return numberOfProducts
      ? `${numberOfProducts} ${
          numberOfProducts === 1 ? translatedData['common']['protectedProduct'].slice(0, -1) : translatedData['common']['protectedProduct']
        }`
      : translatedData['device']['noProtectedProducts']
  },
  planDetailsVariableData1: (translatedData, planDetails) => {
    return `${planDetails.currencySymbol}${translatedData['plan']['amountDueOn'].replace(
      /@amount|@nextBillingDate/g,
      (matched) => planDetails[matched]
    )}`
  },
  planDetailsVariableData2: (translatedData, numberOfProducts) => {
    return numberOfProducts
      ? `${numberOfProducts} ${
          numberOfProducts === 1 ? translatedData['common']['protectedProduct'].slice(0, -1) : translatedData['common']['protectedProduct']
        }`
      : translatedData['device']['noProtectedProducts']
  },
  planDetailsOverview: (translatedData, planDetails) => [
    { label: translatedData.common.purchaseDate, value: getFormattedDate(planDetails?.dateOfPurchase) },
    { label: translatedData.common.coverageStartDate, value: getFormattedDate(planDetails?.coverageStartDate) },
    { label: translatedData.common.nextBillingDate, value: getFormattedDate(planDetails?.nextBillingDate) },
    {
      label: translatedData.subscription.aggregateClaimLimit,
      value: `${planDetails?.currencySymbol}${planDetails?.aggregateClaimLimit}`,
    },
    {
      label: translatedData.common.availableClaimLimit,
      value: `${planDetails?.currencySymbol}${planDetails?.aggregateClaimLimit}`,
    },
    { label: translatedData.common.perClaimLimit, value: `${planDetails?.currencySymbol}${planDetails?.perClaimLimit}` },
  ],
  planDetailsProductHeader: (translatedData) => translatedData['common']['protectedProduct'],
  planDetailsBillingDetailsDescription: (translatedData, planDetails) => {
    return `${translatedData['plan']['amountIsDueOn'].replace(/@amount|@nextBillingDate/g, (matched) => planDetails[matched])}`
  },
  billingHistoryVariableData1: (translatedData) => translatedData['common']['purchaseDate'], // To be added in translation
  billingHistoryVariableData2: (details) =>
    new Date(details['purchaseDate']).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }),
  billingHistoryVariableData3: (translatedData) => translatedData['common']['nextBilling'],
  billingHistoryVariableData4: (planDetails, translatedData) => {
    return `${translatedData['common']['amountIsDueOn'].replace(/@amount|@nextBillingDate/g, (matched) => planDetails[matched])}`
  },
  billingHistoryVariableData5: (translatedData) => ({
    class: 'success',
    text: translatedData.common.active,
  }),
  productAdditionContent1: ['device']['productAddedSuccessfully'],
  productAdditionContent2: (translatedData, details) => {
    return translatedData['device']['productProtectedUnderPlanDescription'].replace(
      /@modelName|@subcategory|@planName/g,
      (matched) => details[matched]
    )
  },
  productAdditionPrimaryCTA: ['common']['okay'],
  productPlanDetailsSectionHeading: ['device']['protectedUnder'],
  // productVariableData1: (details) => details['planName'],
  // productVariableData2: (translatedData) => translatedData['home']['raiseClaim'],
  // showProductVariableData2Button: true,
  // isProductVariableData2Disabled: false
  paymentMethodVariableData: (translatedData, details) =>
    translatedData['payment']['activePlanCollection']?.replace(/@currency|@amount/g, (matched) => {
      return details[matched]
    }),
  claimVariable1: (productDetails, translatedData) => {
    return deviceStatusMapping(
      productDetails.consumerServiceRequestID,
      productDetails.policyDetails[0].planName,
      productDetails.policyDetails[0].planName,
      ''
    )
  },
  claimVariable2: (productDetails, translatedData, showRaiseClaimButton) => {
    return deviceStatusMapping(
      productDetails.consumerServiceRequestID,
      <Button className={styles['btn']} disabled={true}>
        <div className='f12'>{translatedData.device.claimRaisedTitle}</div>
      </Button>,
      showRaiseClaimButton ? (
        <Button className={styles['btn']}>
          <div className='f12'>{translatedData.request.raiseClaim}</div>
        </Button>
      ) : (
        <div className={styles['protected']}>
          <Protected /> {translatedData.common.protected}
        </div>
      ),
      ''
    )
  },
}

const onHoldStatuses = {
  myPlansVariableData1: (translatedData, planDetails) => {
    return `${planDetails.currencySymbol}${translatedData['plan']['amountOverdueFor'].replace(
      /@amount|@lastBillingDate/g,
      (matched) => planDetails[matched]
    )}`
  },
  myPlansVariableData2: (translatedData) => translatedData['plan']['planOnHold'],
  planDetailsVariableData1: (translatedData, planDetails) => {
    return `${planDetails.currencySymbol}${translatedData['plan']['amountOverdueFor'].replace(
      /@amount|@lastBillingDate/g,
      (matched) => planDetails[matched]
    )}`
  },
  planDetailsVariableData2: (translatedData) => translatedData['plan']['planOnHold'],
  planDetailsOverview: (translatedData, planDetails) => [
    { label: translatedData.common.purchaseDate, value: getFormattedDate(planDetails?.dateOfPurchase) },
    { label: translatedData.common.coverageStartDate, value: getFormattedDate(planDetails?.coverageStartDate) },
    { label: translatedData.common.gracePeriodEndDate, value: getFormattedDate(planDetails?.nextBillingDate) },
    {
      label: translatedData.subscription.aggregateClaimLimit,
      value: `${planDetails?.currencySymbol}${planDetails?.aggregateClaimLimit}`,
    },
    {
      label: translatedData.common.availableClaimLimit,
      value: `${planDetails?.currencySymbol}${planDetails?.aggregateClaimLimit}`,
    },
    { label: translatedData.common.perClaimLimit, value: `${planDetails?.currencySymbol}${planDetails?.perClaimLimit}` },
  ],
  planDetailsProductHeader: (translatedData) => translatedData['device']['protectionOnHoldFor'],
  planDetailsBillingDetailsDescription: (translatedData, planDetails) => {
    return `${translatedData['plan']['amountIsOverdueFor'].replace(/@amount|@lastBillingDate/g, (matched) => planDetails[matched])}`
  },
  billingHistoryVariableData1: (translatedData) => translatedData['common']['purchaseDate'], // To be added in translation
  billingHistoryVariableData2: (details) =>
    new Date(details['purchaseDate']).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }),
  billingHistoryVariableData3: (translatedData) => translatedData['common']['paymentOverdue'],
  billingHistoryVariableData4: (planDetails, translatedData) => {
    return `${planDetails.currencySymbol}${translatedData['common']['amountDueUntil'].replace(
      /@amount|@gracePeriodEndDate/g,
      (matched) => planDetails[matched]
    )}`
  },
  billingHistoryVariableData5: (translatedData) => ({
    class: 'success',
    text: translatedData.common.active,
  }),
  productAdditionContent1: ['device']['productAddedSuccessfully'],
  productAdditionContent2: (translatedData, details) => {
    return translatedData['device']['productProtectedOnHoldDescription'].replace(/@modelName|@subcategory/g, (matched) => details[matched])
  },
  productAdditionPrimaryCTA: ['common']['updatePaymentMethod'],
  productPlanDetailsSectionHeading: ['device']['protectionOnHold'],
  claimVariable1: (productDetails, translatedData) => {
    return deviceStatusMapping(
      productDetails.consumerServiceRequestID,
      productDetails.policyDetails[0].planName,
      translatedData.device.updatePaymentMethod,
      ''
    )
  },
  claimVariable2: (productDetails, translatedData, showRaiseClaimButton) => {
    return deviceStatusMapping(
      productDetails.consumerServiceRequestID,
      <Button className={styles['btn']} disabled={true}>
        <div className='f12'>{translatedData.device.claimRaisedTitle}</div>
      </Button>,
      <div className={styles['on-hold']}>
        <OnHold /> {translatedData.device.protectionOnHold}
      </div>,
      ''
    )
  },
}

const onCancelledStatuses = {
  planDetailsVariableData1: (translatedData, planDetails) => {
    return `${translatedData['plan']['coverageEndedOn'].replace(/@coverageEndDate/g, (matched) => planDetails[matched])}`
  },
  billingHistoryVariableData1: (translatedData) => translatedData['common']['cancellationDate'],
  billingHistoryVariableData2: (details) => getFormattedDate(details['cancellationDate']),
  billingHistoryVariableData3: (translatedData) => translatedData['common']['coverageEndDate'],
  billingHistoryVariableData4: (planDetails) => planDetails['coverageEndDate'],
  billingHistoryVariableData5: (translatedData) => ({
    class: 'success',
    text: translatedData.common.active,
  }),
  productAdditionContent1: ['device']['productAddedSuccessfully'],
  productAdditionContent2: (translatedData, details) => {
    return translatedData['device']['productNotProtectedDescription'].replace(/@modelName|@subcategory/g, (matched) => details[matched])
  },
  productAdditionPrimaryCTA: ['common']['buyPlan'],
  claimVariable1: (productDetails, translatedData) => {
    return deviceStatusMapping(
      productDetails.consumerServiceRequestID,
      translatedData.device.buyAPlanToProtect,
      productDetails.policyDetails[0].planName,
      ''
    )
  },
  claimVariable2: (productDetails, translatedData, showRaiseClaimButton) => {
    return deviceStatusMapping(
      productDetails.consumerServiceRequestID,
      <div className={styles['not-protected']}>
        <NotProtected /> {translatedData.device.notProtected}
      </div>,
      <Button className={styles['btn']} disabled={true}>
        <div className='f12'>{translatedData.device.claimRaisedTitle}</div>
      </Button>,
      ''
    )
  },
}

export const singleProductPlanStatuses = {
  1: {
    isActive: true,
    statusTextKey: 'active',
    statusText: 'Active',
    styles: {
      backgroundColor: 'var(--success-200)',
      color: 'var(--success-700)',
    },
    ...activeStatuses,
    planDetailsOverview: (translatedData, planDetails) => [
      { label: translatedData.common.purchaseDate, value: getFormattedDate(planDetails?.dateOfPurchase) },
      { label: translatedData.common.coverageStartDate, value: getFormattedDate(planDetails?.coverageStartDate) },
      { label: translatedData.common.coverageEndDate, value: getFormattedDate(planDetails?.coverageEndDate) },
    ],
  },
  '-9': {
    isOnHold: true,
    statusTextKey: 'onHold',
    statusText: 'On Hold',
    styles: {
      backgroundColor: 'var(--light-beige-700)',
      color: 'var(--orange-700)',
    },
    ...onHoldStatuses,
    planDetailsOverview: (translatedData, planDetails) => [
      { label: translatedData.common.purchaseDate, value: getFormattedDate(planDetails?.dateOfPurchase) },
      { label: translatedData.common.coverageStartDate, value: getFormattedDate(planDetails?.coverageStartDate) },
      { label: translatedData.common.coverageEndDate, value: getFormattedDate(planDetails?.coverageEndDate) },
    ],
  },
  '-3': {
    isExpired: true,
    statusTextKey: 'expired',
    statusText: 'Expired',
    styles: {
      backgroundColor: 'var(--white-500)',
      color: 'var(--black-500)',
    },
    ...onCancelledStatuses,
  },
  '-4': {
    isCancelled: true,
    statusTextKey: 'cancelled',
    statusText: 'Cancelled',
    styles: {
      backgroundColor: 'var(--error-200)',
      color: 'var(--error-700)',
    },
    ...onCancelledStatuses,
  },
}

export const planStatuses = {
  // Plan Purchased ==> soldPlan status = 1 , groupSoldPlan status = 1
  // Plan On Hold ==> soldPlan status = -9 ,
  // groupSoldPlan status = -9
  // Plan under cancellation ===> soldPlan status = 1 , groupSoldPlan status = -5
  // Plan Cancelled ==> soldPlan status = -4,
  // groupSoldPlan status = -3
  1: {
    status: 1,
    1: {
      groupStatus: 1,
      isActive: true,
      statusText: 'Active',
      ...activeStatuses,
    },
    '-5': {
      groupStatus: -5,
      isUnderCancellation: true,
      statusText: 'Under Cancellation',
      myPlansVariableData1: (translatedData, planDetails) => {
        return `${planDetails.currencySymbol}${translatedData['plan']['coverageWillEndOn'].replace(
          /@amount|@coverageEndDate/g,
          (matched) => planDetails[matched]
        )}`
      },
      myPlansVariableData2: (translatedData, numberOfProducts) => {
        return numberOfProducts
          ? `${numberOfProducts} ${
              numberOfProducts === 1
                ? translatedData['common']['protectedProduct'].slice(0, -1)
                : translatedData['common']['protectedProduct']
            }`
          : translatedData['device']['noProtectedProducts']
      },
      planDetailsVariableData1: (translatedData, planDetails) => {
        return `${planDetails.currencySymbol}${translatedData['plan']['coverageWillEndOn'].replace(
          /@amount|@coverageEndDate/g,
          (matched) => planDetails[matched]
        )}`
      },
      planDetailsVariableData2: (translatedData, numberOfProducts) => {
        return numberOfProducts
          ? `${numberOfProducts} ${
              numberOfProducts === 1
                ? translatedData['common']['protectedProduct'].slice(0, -1)
                : translatedData['common']['protectedProduct']
            }`
          : translatedData['device']['noProtectedProducts']
      },
      planDetailsOverview: (translatedData, planDetails) => [
        { label: translatedData.common.cancellationDate, value: getFormattedDate(planDetails?.planCancellationDate) },
        { label: translatedData.common.coverageEndDate, value: getFormattedDate(planDetails?.coverageEndDate) },
        {
          label: translatedData.subscription.aggregateClaimLimit,
          value: `${planDetails?.currencySymbol}${planDetails?.aggregateClaimLimit}`,
        },
        {
          label: translatedData.common.availableClaimLimit,
          value: `${planDetails?.currencySymbol}${planDetails?.aggregateClaimLimit}`,
        },
        { label: translatedData.common.perClaimLimit, value: `${planDetails?.currencySymbol}${planDetails?.perClaimLimit}` },
      ],
      planDetailsProductHeader: (translatedData) => translatedData['common']['protectedProduct'],
      planDetailsBillingDetailsDescription: (translatedData) => translatedData['plan']['billingCeased'],
      billingHistoryVariableData1: (translatedData) => translatedData['common']['cancellationDate'],
      billingHistoryVariableData2: (details) =>
        new Date(details['cancellationDate']).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        }),
      billingHistoryVariableData3: (translatedData) => translatedData['common']['coverageEndDate'],
      billingHistoryVariableData4: (details) => details['coverageEndDate'],
      billingHistoryVariableData5: (translatedData) => ({
        class: 'success',
        text: translatedData.common.active,
      }),
      productAdditionContent1: ['device']['productAddedSuccessfully'],
      productAdditionContent2: (translatedData, details) => {
        return translatedData['device']['productProtectedUnderPlanDescription'].replace(
          /@modelName|@subcategory|@planName/g,
          (matched) => details[matched]
        )
      },
      productAdditionPrimaryCTA: ['common']['okay'],
      productPlanDetailsSectionHeading: ['device']['protectedUnder'],
      // productVariableData1: (details) => details['planName'],
      // productVariableData2: (translatedData) => translatedData['home']['raiseClaim'],
      // showProductVariableData2Button: true,
      // isProductVariableData2Disabled: false
      claimVariable1: (productDetails, translatedData) => {
        return deviceStatusMapping(
          productDetails.consumerServiceRequestID,
          productDetails.policyDetails[0].planName,
          productDetails.policyDetails[0].planName,
          ''
        )
      },
      claimVariable2: (productDetails, translatedData, showRaiseClaimButton) => {
        return deviceStatusMapping(
          productDetails.consumerServiceRequestID,
          <Button className={styles['btn']} disabled={true}>
            <div className='f12'>{translatedData.device.claimRaisedTitle}</div>
          </Button>,
          showRaiseClaimButton ? (
            <Button className={styles['btn']}>
              <div className='f12'>{translatedData.home.raiseClaim}</div>
            </Button>
          ) : (
            <div className={styles['protected']}>
              <Protected /> {translatedData.common.protected}
            </div>
          ),
          ''
        )
      },
    },
  },
  '-9': {
    status: -9,
    '-9': {
      groupStatus: -9,
      isOnHold: true,
      statusText: 'On Hold',
      ...onHoldStatuses,
    },
  },
  '-4': {
    status: -4,
    '-3': {
      groupStatus: -3,
      isCancelled: true,
      statusText: 'Cancelled',
      ...onCancelledStatuses,
    },
  },
}
